/**
 * Implementation primitives for the cluster layer — layer
 * registration helpers, click-handler logic, viewport-aware
 * counters. Split from :file:`cluster-layer.tsx` so the
 * ``react-refresh/only-export-components`` lint rule stays
 * satisfied (the ``.tsx`` file exports the React component
 * exclusively, mirroring :file:`snapshot-layer-impl.ts`).
 *
 * Cross-references:
 *   - REQ-UI-010 / REQ-UI-011.
 *   - REQ-UI-059 (a11y region drives the cluster summary).
 *   - ADR-016 Open Item 5 (cluster z 0–6 / individual z 7+).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md.
 */
import type { GeoJSONSource, MapGeoJSONFeature, Map as MapLibreMap } from "maplibre-gl";
import {
  CLUSTER_COUNT_LAYER_ID,
  CLUSTER_FILL_OPACITY,
  CLUSTER_LABEL_COLOR,
  CLUSTER_LABEL_HALO_COLOR,
  CLUSTER_LABEL_HALO_WIDTH_PX,
  CLUSTER_LABEL_SIZE_PX,
  CLUSTER_LAYER_ID,
  CLUSTER_STROKE_WIDTH_PX,
  clusterColorExpression,
  clusterRadiusExpression,
} from "@/map/cluster-styling";
import { SNAPSHOT_LAYER_ID, SNAPSHOT_SOURCE_ID } from "@/map/snapshot-layer-impl";
import { isMapUsable } from "@/map/map-ready";
import type { PlatformColorConfig } from "@/map/marker-styling";

/**
 * Debounce window for the a11y summary recompute, in milliseconds.
 *
 * Exported as a constant for test reach + visibility. Orchestrator
 * decision 5 sets this to ~500 ms so panning doesn't thrash the
 * screen reader.
 *
 * Cross-references:
 *   - REQ-UI-010 / REQ-UI-059.
 */
export const CLUSTER_SUMMARY_DEBOUNCE_MS = 500;

/**
 * Cluster click duration in milliseconds — animates the ``flyTo``
 * to the cluster's expansion zoom. Per orchestrator decision 6
 * (storyboard-implied; tunable later).
 *
 * Cross-references:
 *   - REQ-UI-010.
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Frame 2 — Operator zooms in; clusters resolve".
 */
export const CLUSTER_CLICK_FLY_DURATION_MS = 400;

/**
 * Layer-registration side-effect — register the cluster circle +
 * cluster-count symbol layers above the snapshot's marker layer.
 *
 * Exported for unit testability. Idempotent: subsequent calls
 * detect the existing layers and no-op the *add* rather than re-adding
 * (re-adding would throw per MapLibre's spec) — but they DO re-apply the
 * cluster bubble's ``circle-color`` so a theme flip or a Profile
 * ``map.platform`` colour change repaints the live bubble in place
 * (``platformColor``; REQ-UI-086 / REQ-UI-040), mirroring the snapshot layer's
 * existing-source re-paint. Omit the arg for the legacy ``by_status`` colour.
 *
 * Cross-references:
 *   - REQ-UI-010 / REQ-UI-011 / REQ-UI-086.
 */
export function attachClusterLayers(map: MapLibreMap, platformColor?: PlatformColorConfig): void {
  // No-op safely on a REMOVED map (teardown race) — see :file:`map/map-ready.ts`;
  // CLAUDE.md §8.1 fail-safe. A still-*loading* map is deliberately usable here:
  // :class:`cluster-layer.ClusterLayer`'s attach effect keys on the
  // ``hasSnapshot`` boolean and fires exactly once (when the snapshot first
  // arrives), which commonly lands inside the style-load window — gating that
  // one shot on ``isStyleLoaded()`` blocked the cluster layers permanently and
  // nothing painted at zoom ≤ CLUSTER_MAX_ZOOM (the low-zoom-clusters
  // regression). ``addLayer`` is valid while the style loads; MapLibre applies
  // it when the style settles. Do NOT re-tighten this to a loaded-style guard.
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(CLUSTER_LAYER_ID) === undefined) {
    map.addLayer({
      id: CLUSTER_LAYER_ID,
      type: "circle",
      source: SNAPSHOT_SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": clusterColorExpression(platformColor),
        "circle-radius": clusterRadiusExpression(),
        // Semi-transparent fill so the basemap reads *through* the cluster
        // bubble — the operator keeps spatial context (terrain / coastlines
        // under a dense cluster) rather than a hard opaque disc punching a
        // hole in the map. The status colour stays legible at this opacity on
        // both light and dark basemaps, and the count label's own white halo
        // (CLUSTER_LABEL_HALO_*) keeps the glyph fully readable over the fill.
        "circle-opacity": CLUSTER_FILL_OPACITY,
        // A very thin bounding outline in the same status colour (full opacity →
        // slightly crisper than the translucent fill) gives the bubble a defined
        // edge against the basemap (2026-06-12). Deliberately hairline — the
        // earlier 2 px WHITE stroke read as a heavy outline that fought the map.
        "circle-stroke-width": CLUSTER_STROKE_WIDTH_PX,
        "circle-stroke-color": clusterColorExpression(platformColor),
      },
    });
  } else {
    // Already registered — re-apply the fill + outline colour (both track the
    // status palette) so a Profile mode/colour change or theme flip repaints the
    // live bubble in place without a teardown.
    const color = clusterColorExpression(platformColor);
    map.setPaintProperty(CLUSTER_LAYER_ID, "circle-color", color);
    map.setPaintProperty(CLUSTER_LAYER_ID, "circle-stroke-color", color);
  }
  // The cluster-count symbol layer renders the ``point_count_abbreviated``
  // text label inside the cluster circle (Flow 03 Frame 1 "count
  // rendered in white inside"). MapLibre symbol layers require a
  // ``glyphs`` source URL in the style to fetch the SDF font atlas.
  // Issue #205 added the same-origin BFF glyphs endpoint
  // (``/api/glyphs/{fontstack}/{range}.pbf``, served by
  // :mod:`meridian_bff._glyphs`) and :func:`buildMapStyle` now sets
  // ``style.glyphs`` to it (REQ-UI-035 — same-origin only). The
  // pre-M4 ``hasGlyphs`` conditional gate is therefore retired: the
  // count symbol layer attaches unconditionally.
  //
  // Cross-references:
  //   - Issue #205 (BFF glyphs endpoint — now landed).
  //   - REQ-UI-010 (cluster count indicator).
  //   - REQ-UI-035 (BFF-only; the glyph source is same-origin).
  if (map.getLayer(CLUSTER_COUNT_LAYER_ID) === undefined) {
    map.addLayer({
      id: CLUSTER_COUNT_LAYER_ID,
      type: "symbol",
      source: SNAPSHOT_SOURCE_ID,
      filter: ["has", "point_count"],
      layout: {
        // MapLibre native ``point_count_abbreviated`` emits "1.2k"
        // for 1200, "12k" for 12000, etc. — per orchestrator
        // decision 4.
        "text-field": ["get", "point_count_abbreviated"],
        // Keys into the BFF glyph atlas. "DM Sans Medium" is the
        // UI's body font (already vendored, OFL-licensed) and is
        // the fontstack issue #205's generator builds. The previous
        // default "Open Sans Semibold,Arial Unicode MS Bold" was
        // dropped — Arial Unicode MS is proprietary (Monotype) and
        // can't be bundled per the LICENSING.md free-for-use rule.
        "text-font": ["DM Sans Medium"],
        "text-size": CLUSTER_LABEL_SIZE_PX,
        // Don't let MapLibre hide the count label when it overlaps
        // adjacent labels — the cluster glyph IS the operator's
        // affordance to know "this thing has N members".
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": CLUSTER_LABEL_COLOR,
        // White halo so the dark count reads against any status fill
        // (issue #205 — 2026-05-28 legibility fix). The dark ink keeps
        // WCAG AA on every fill; the halo lifts it off the mid-tone
        // green that the visual check found too faint.
        "text-halo-color": CLUSTER_LABEL_HALO_COLOR,
        "text-halo-width": CLUSTER_LABEL_HALO_WIDTH_PX,
      },
    });
  }
}

/**
 * Detach the cluster layers. Mirrors :func:`detachSnapshotLayer`'s
 * order discipline — symbol layer first, then circle layer; both
 * before the source can be removed. The source itself is owned by
 * the snapshot layer; this function leaves it intact.
 *
 * Cross-references:
 *   - REQ-UI-010.
 */
export function detachClusterLayers(map: MapLibreMap): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(CLUSTER_COUNT_LAYER_ID) !== undefined) {
    map.removeLayer(CLUSTER_COUNT_LAYER_ID);
  }
  if (map.getLayer(CLUSTER_LAYER_ID) !== undefined) {
    map.removeLayer(CLUSTER_LAYER_ID);
  }
}

/**
 * Cluster click → expansion-zoom flyTo. Exported for unit-test
 * isolation; the click-handler effect in :file:`cluster-layer.tsx`
 * binds this to MapLibre.
 *
 * MapLibre's :meth:`GeoJSONSource.getClusterExpansionZoom` returns
 * the zoom level at which the cluster's children would no longer
 * cluster. ``flyTo`` animates the camera to the cluster's
 * coordinates at that zoom; the storyboard Frame 2 describes the
 * expand-on-click behavior (180 ms ease-out animation).
 *
 * Cross-references:
 *   - REQ-UI-010.
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Frame 2 — Operator zooms in; clusters resolve".
 */
export function handleClusterClick(map: MapLibreMap, feature: MapGeoJSONFeature): void {
  const clusterId = feature.properties?.cluster_id as number | undefined;
  if (clusterId === undefined) {
    return;
  }
  const source = map.getSource(SNAPSHOT_SOURCE_ID) as GeoJSONSource | undefined;
  if (source === undefined) {
    return;
  }
  const geometry = feature.geometry;
  if (geometry.type !== "Point") {
    return;
  }
  const [lng, lat] = geometry.coordinates;
  // ``getClusterExpansionZoom`` is async in modern MapLibre — it
  // returns a :class:`Promise<number>`. Older versions accept a
  // callback; we use the promise form for cleaner error surfacing.
  source
    .getClusterExpansionZoom(clusterId)
    .then((zoom) => {
      map.flyTo({
        center: [lng, lat],
        zoom,
        duration: CLUSTER_CLICK_FLY_DURATION_MS,
      });
    })
    .catch((err: unknown) => {
      // Surface loudly per CLAUDE.md §8.1 — a broken expansion
      // path means clicks silently do nothing, which is worse
      // than a console error.
      console.error("[meridian/map/cluster-layer] getClusterExpansionZoom failed:", err);
    });
}

/**
 * Compute the cluster + producer counts from the current viewport.
 *
 * Queries MapLibre for the cluster features visible at this moment
 * + the individual marker features (post-filter). Sums their
 * ``point_count`` to get the total Producer count.
 *
 * Exported for unit testability + so the agent's verification probe
 * can confirm the counts independently.
 *
 * Cross-references:
 *   - REQ-UI-010 / REQ-UI-059.
 */
export function computeClusterCounts(map: MapLibreMap): {
  clusterCount: number;
  producerCount: number;
} {
  // No-op safely on a removed / not-ready map. This runs from a debounced
  // ``setTimeout`` fed by ``moveend`` / ``sourcedata`` — genuinely capable of
  // firing after the map was removed; ``getLayer`` would then throw on
  // ``map.style`` (see :file:`map/map-ready.ts`). Quiet zero-counts beat a
  // crash for an a11y summary (CLAUDE.md §8.1).
  if (!isMapUsable(map)) {
    return { clusterCount: 0, producerCount: 0 };
  }
  // Guard against the layers being absent (race during teardown).
  // Per MapLibre's queryRenderedFeatures contract, missing layers
  // throw; the guards keep the a11y region quiet rather than noisy.
  const clusterLayerPresent = map.getLayer(CLUSTER_LAYER_ID) !== undefined;
  const snapshotLayerPresent = map.getLayer(SNAPSHOT_LAYER_ID) !== undefined;
  if (!clusterLayerPresent && !snapshotLayerPresent) {
    return { clusterCount: 0, producerCount: 0 };
  }
  let clusterCount = 0;
  let aggregatedProducerCount = 0;
  if (clusterLayerPresent) {
    const clusters = map.queryRenderedFeatures(undefined, {
      layers: [CLUSTER_LAYER_ID],
    });
    clusterCount = clusters.length;
    for (const f of clusters) {
      const pc = f.properties?.point_count as number | undefined;
      if (typeof pc === "number") {
        aggregatedProducerCount += pc;
      }
    }
  }
  let individualCount = 0;
  if (snapshotLayerPresent) {
    individualCount = map.queryRenderedFeatures(undefined, {
      layers: [SNAPSHOT_LAYER_ID],
    }).length;
  }
  return {
    clusterCount,
    producerCount: aggregatedProducerCount + individualCount,
  };
}
